'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { numberFormatter } from '../../lib/util'
import { useDispatch, useSelector } from 'react-redux'
import { updateInvoiceForm } from './../redux/reducers/invoiceSlice'


export default function InvoiceFormClientHandler({ intentKey }) {
  
  const router = useRouter()
  const dispatch = useDispatch()
  const { selectedTemplateId } = useSelector(state => state.invoice)
  
  useEffect(() => {
    const USER_LOCALE = navigator.language || navigator.userLanguage // e.g., "en-US", "en-IN"
    const form = document.getElementById('invoice-form')
    if (!form) return

    const handleSubmit = (e) => {
      e.preventDefault()

      const formData = new FormData(form)
      const formErrors = {}
      const numberFromString = (str) => Number(str.replace(/,/g, ''))

      // all selectors here
      const senderInfoEl = document.querySelector('.sender-info')
      const recepientInfoEl = document.querySelector('.recipient-info')
      const currencySelectEl = document.getElementById('invoice-currency')
      const summarySubtotalEl = document.getElementById('invoice-final-subtotal')
      const totalTaxEl = document.getElementById('invoice-final-tax')
      const finalPriceEl = document.getElementById('invoice-final-price')
      const invoiceTermsEl = document.getElementById('invoice-terms')
      const errorFormEl = document.getElementById('form-errors')


      // extracting sender and recepient data from UI 
      const senderDetails = Array.from(senderInfoEl.querySelectorAll('p')).reduce((acc, p) => {
        acc[p.id] = p.textContent.trim()
        return acc
      }, {})

      const recepientDetails = Array.from(recepientInfoEl.querySelectorAll('p')).reduce((acc, p) => {
        acc[p.id] = p.textContent.trim()
        return acc
      }, {})
      
      const { fromname, ...senderExtraFields } = senderDetails
      const { toname, ...recepientExtraFields } = recepientDetails

      // extracting invoice item data from UI based on the invoiceRowSelector (whether ssg or csr)
      const extractInvoiceItemsFromTable = (invoiceRowSelector) => Array.from(document.querySelector(invoiceRowSelector).children).map((invoiceItemRow) => {
        const inputs = invoiceItemRow.querySelectorAll('input')
        const descriptionTextarea = invoiceItemRow.querySelector('textarea')
        const subTotalSpan = invoiceItemRow.querySelectorAll('span[data-for]')
        const hasErrorClass = invoiceItemRow.classList.contains('bg-orange-100')
        let foundErrors =  false
        const eachRow = {}

        // extract value of description textarea and set it in 'eachRow' object
        eachRow['item-description'] = descriptionTextarea.value

        // populate value of all inputs present in current row (invoiceItemRow) in 'eachRow' object
        inputs.forEach((input) => {
          const isFieldForItemName = input.getAttribute('data-for') === "item-name"
          const isFieldForItemQuantity = input.getAttribute('data-for') === "item-quantity"
          const isFieldForItemUnitPrice = input.getAttribute('data-for') === "item-unit-price"
          const isFieldForTaxType = input.getAttribute('data-for') === "item-tax-type"

          // error checking logic here
          if (isFieldForItemName && !input?.value) {
            formErrors["item-name"] = 'Must have an item name '
            invoiceItemRow.classList.add('bg-orange-100', 'hover:bg-orange-200')
            foundErrors = true
          }

          // error checking logic here
          if (isFieldForItemQuantity && input?.value < 1) {
            formErrors["item-quantity"] = 'Item Quantity must be more than 0 '
            invoiceItemRow.classList.add('bg-orange-100', 'hover:bg-orange-200')
            foundErrors = true
          }

          // error checking logic here
          if (isFieldForItemUnitPrice && input?.value < 1) {
            formErrors["item-unit-price"] = 'Item unit price must be more than 0 '
            invoiceItemRow.classList.add('bg-orange-100', 'hover:bg-orange-200')
            foundErrors = true
          }

          // extract value of input field and set it in 'eachRow' object
          const valueOfInput = (isFieldForItemName || isFieldForItemQuantity || isFieldForTaxType) ? input.value : numberFormatter(input.value, USER_LOCALE)
          eachRow[input.getAttribute('data-for')] = valueOfInput
        })

        if (eachRow['item-tax-type'] === 'percentage') {
          eachRow['tax-fixed-value'] = numberFromString(eachRow['item-tax']) * numberFromString(eachRow['item-quantity']) * numberFromString(eachRow['item-unit-price']) / 100
        } else {
          eachRow['tax-fixed-value'] = numberFromString(eachRow['item-tax'])
        }

        if (!foundErrors && hasErrorClass) invoiceItemRow.classList.remove('bg-orange-100', 'hover:bg-orange-200')
        
        // populate value of sub-total for current row (invoiceItemRow) in 'eachRow' object
        Array.from(subTotalSpan).forEach(span => eachRow[span.getAttribute('data-for')] = span.textContent.trim())
        return eachRow
      })

      const issueDateInput = document.getElementById("issue-date")
      const dueDateInput = document.getElementById("due-date")
      const issueDateValue = issueDateInput.value ? new Date(issueDateInput.value).toISOString() : null
      const dueDateValue = dueDateInput.value ? new Date(dueDateInput.value).toISOString() : null

      const allItemsSsg = extractInvoiceItemsFromTable('.invoice-row-ssg')
      const allItemsCsr = extractInvoiceItemsFromTable('.invoice-row-csr')
      const currencySelectValue = currencySelectEl.value
      const summarySubtotal = summarySubtotalEl.textContent.trim()
      const totalTax = totalTaxEl.textContent.trim()
      const finalPrice = finalPriceEl.textContent.trim()
      const invoiceTerms = invoiceTermsEl.value

      if (!formData.get('invoice-no')) formErrors['invoice-no'] = 'Invoice number is required'
      if (!fromname) formErrors['from-name'] = 'Invoice sender\'s name is required'
      if (!toname) formErrors['to-name'] = 'Invoice recepient name is required'
      if (!issueDateValue) formErrors['issue-date'] = 'Issue date is required'

      // error checking logic here
      if (Object.keys(formErrors).length > 0) {
        // Show error message under textarea
        errorFormEl.classList.remove('hidden')
        const errorElements = Object.entries(formErrors).reduce((acc, [field, msg]) => {
          const errorItem = `<li class='field-error'>${msg}</li>`
          return (acc + errorItem)
        }, '')
        const errorList = document.getElementById('error-list')
        errorList.innerHTML = errorElements
        return
      } else {
        const isHidden = errorFormEl.classList.contains('hidden') // check if the error container is hidden
        const elementToRemove = document.querySelectorAll('.field-error')
        if (elementToRemove && !isHidden) {
          errorFormEl.classList.add('hidden')
          Array.from(elementToRemove).forEach(el => el.remove())
        }
      }

      const invoiceObj = {
        invoiceNo: formData.get('invoice-no'),
        issueDateValue,
        dueDateValue,
        senderDetails: {
          senderName: senderDetails.fromname,
          senderExtraFields: Object.entries(senderExtraFields).map(([key, val]) => ({key, val}))
        },
        recepientDetails: {
          recepientName: recepientDetails.toname,
          recepientExtraFields: Object.entries(recepientExtraFields).map(([key, val]) => ({key, val}))
        },
        allItems: [...allItemsSsg, ...allItemsCsr],
        invoiceSummary: {
          currency: currencySelectValue,
          summarySubtotal,
          totalTax,
          finalPrice
        },
        invoiceTerms
      }

      dispatch(updateInvoiceForm(invoiceObj))

      // Google Tag Manager - Track invoice creation conversion
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'invoice_created',
          'event_category': 'engagement',
          'event_label': intentKey,
          'value': finalPrice,
          'currency': currencySelectValue,
          'invoice_type': intentKey,
          'invoice_amount': finalPrice
        })
      }

      // we re-direct user to download page if they have already selected a template (i.e. they are coming from choose templates page)
      // otherwise, we redirect them to choose a template
      if (selectedTemplateId) router.push(`/invoice/download/download-${intentKey}`)
      else router.push(`/invoice/choose/choose-${intentKey}`) 
    }

    form.addEventListener('submit', handleSubmit)

    return () => {
      form.removeEventListener('submit', handleSubmit)
    }
  }, [])

  return null
}
